import { apiClient } from './apiClient';
import type { OpportunitySignal } from '../types/opportunitySignal';

export type LeadSuggestionStatus =
  | 'queued'
  | 'suggested'
  | 'accepted'
  | 'rejected'
  | 'archived'
  | 'converted'
  | 'saved'
  | 'dismissed'
  | 'duplicate'
  | 'expired';

export type LeadSource =
  | 'apollo'
  | 'perplexity'
  | 'apollo_perplexity'
  | 'google_places'
  | 'manual_csv'
  | 'website_form';

export type RejectionReason =
  | 'not_relevant'
  | 'duplicate'
  | 'outside_service_area'
  | 'bad_contact_info'
  | 'already_contacted';

export interface ScoringWeights {
  customer_type_match: number;
  location_match: number;
  company_size_match: number;
  website_quality_signal: number;
  reputation_signal: number;
  decision_maker_found: number;
  growth_or_urgency_signal: number;
}

export interface LeadPreferencePresetOptions {
  services_offered: string[];
  ideal_customer_types: string[];
  target_company_sizes: string[];
  lead_qualification_priorities: string[];
  default_scoring_weights: ScoringWeights;
  rejection_reasons: RejectionReason[];
  source_labels: Record<string, string>;
}

export interface OrganizationLeadPreferences {
  enabled: boolean;
  target_business_types: string[];
  target_locations: string[];
  target_company_sizes: string[];
  preferred_buyer_roles: string[];
  excluded_industries: string[];
  excluded_cities: string[];
  excluded_keywords: string[];
  daily_lead_limit: number;
  daily_lead_suggestion_count: number;
  priority_preferences: string[];
  lead_qualification_priorities: string[];
  custom_notes: string;
  business_type: string;
  services_offered: string[];
  custom_services: string[];
  ideal_customer_types: string[];
  custom_customer_types: string[];
  primary_city: string;
  state: string;
  counties: string[];
  radius_miles: number | null;
  zip_codes: string[];
  scoring_weights: ScoringWeights;
  auto_select_lead_sources: boolean;
  manual_lead_sources: string[];
  min_lead_score_threshold: number;
  lead_engine_mode?: 'legacy' | 'waterfall';
  daily_lead_target?: number;
  customer_type_rotation_index?: number;
  available_sources: string[];
  selected_sources: string[];
  source_selection_mode: string;
  source_selection_warnings: string[];
  integrations_message: string;
  plan_daily_lead_limit: number;
  max_selectable_daily_lead_count: number;
  plan_tier: string;
  daily_lead_count_options: number[];
  preferences_complete: boolean;
  preset_options: LeadPreferencePresetOptions;
  created_at: string;
  updated_at: string;
}

export type LeadSuggestionSettings = OrganizationLeadPreferences;

export interface LeadSuggestion {
  id: string;
  company_name: string;
  industry: string;
  location: string;
  address: string;
  city: string;
  state: string;
  postal_code: string;
  company_size: string;
  contact_name: string;
  contact_role: string;
  decision_maker_name: string;
  decision_maker_title: string;
  decision_maker_email: string;
  email: string;
  phone: string;
  website: string;
  source_url: string;
  lead_source: LeadSource;
  source_used: string;
  source_record_id: string;
  source_confidence: string;
  source_attribution: string;
  rating: string | null;
  review_count: number | null;
  business_status: string;
  why_this_lead: string;
  reason_recommended: string;
  recommendation_basis: string;
  recommended_service_angle: string;
  suggested_first_email: string;
  suggested_follow_up: string;
  notes: string;
  estimated_fit: string;
  priority: 'low' | 'medium' | 'high';
  confidence_level: string;
  lead_score: number;
  confidence: string;
  opportunity_signals?: OpportunitySignal[];
  enrichment_status?: 'not_attempted' | 'succeeded' | 'partial' | 'no_data' | 'failed';
  score_basis?: 'base' | 'enriched';
  signals_present?: string[];
  status: LeadSuggestionStatus;
  contact_status?: string;
  rejection_reason: string;
  matched_existing_lead: string | null;
  generated_at: string | null;
  accepted_at: string | null;
  converted_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface EmailSuggestion {
  id: string;
  email_type: string;
  subject: string;
  body: string;
  cta_suggestion: string;
  provider: string;
  lead_suggestion: string | null;
  created_at: string;
  updated_at: string;
}

interface PaginatedLeadSuggestionResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: LeadSuggestion[];
}

export interface BroadenHint {
  radius_miles: number | null;
  suggested_radius_miles: number;
  counties: string[];
  zip_codes: string[];
  target_company_sizes: string[];
  sizes_excluded: string[];
  has_narrow_geography: boolean;
}

export type LeadSuggestionOutcomeCode =
  | 'success'
  | 'pool_exhausted'
  | 'all_duplicates'
  | 'daily_cap_reached'
  | 'no_sources'
  | 'preflight_unconfigured'
  | 'quota_exhausted'
  | 'all_candidates_filtered'
  | 'no_candidates_from_sources'
  | 'partial'
  | 'pool_exhausted_below_target';

export type LeadSuggestionTerminalReason =
  | 'pool_exhausted'
  | 'quota_exceeded'
  | 'all_below_threshold'
  | 'no_candidates_in_territory'
  | 'daily_cap_reached'
  | 'preflight_failed'
  | 'no_sources'
  | 'all_duplicates'
  | 'all_filtered_structural';

// Plain-language, actionable copy for each terminal reason. Never render the raw enum.
export const TERMINAL_REASON_COPY: Record<LeadSuggestionTerminalReason, string> = {
  pool_exhausted: "You've seen all the leads in your area for now — new ones appear as they're found.",
  quota_exceeded: 'The daily discovery limit was reached. Fresh leads will resume tomorrow.',
  all_below_threshold: 'Leads were found, but none met your quality bar. Try widening your preferences.',
  no_candidates_in_territory: 'No businesses matched in your area. Try broadening your location or customer types.',
  daily_cap_reached: "You're all caught up — your lead pool is already full for today.",
  preflight_failed: 'Finish setting up your lead preferences to start receiving leads.',
  no_sources: 'No lead sources are connected yet. Connect a source to start discovering leads.',
  all_duplicates: 'Every lead found today was already in your pipeline.',
  all_filtered_structural: 'Leads were found but filtered out by your industry, geography, or exclusion settings.',
};

export function terminalReasonCopy(reason?: string | null): string | null {
  if (!reason) return null;
  return TERMINAL_REASON_COPY[reason as LeadSuggestionTerminalReason] ?? null;
}

export interface LeadSuggestionRunCounts {
  candidates: number;
  duplicates: number;
  created: number;
  rejected: number;
}

export interface LeadSuggestionGenerateResult {
  run_id: string;
  outcome_code: LeadSuggestionOutcomeCode | string;
  terminal_reason?: LeadSuggestionTerminalReason | string | null;
  status: string;
  counts: LeadSuggestionRunCounts;
  candidates_found: number;
  suggestions_created: number;
  duplicates_skipped: number;
  duplicate_reasons?: Record<string, number>;
  error_message?: string;
  cooldown_seconds?: number;
  broaden_hint?: BroadenHint;
  surfaced_count?: number;
  pool_count?: number;
}

export const leadSuggestionService = {
  async getLeadPreferences(): Promise<OrganizationLeadPreferences> {
    return apiClient.get('/organization/lead-preferences/');
  },

  async updateLeadPreferences(payload: Partial<OrganizationLeadPreferences>): Promise<OrganizationLeadPreferences> {
    return apiClient.patch('/organization/lead-preferences/', payload);
  },

  async getSettings(): Promise<LeadSuggestionSettings> {
    return apiClient.get('/lead-suggestions/settings/');
  },

  async updateSettings(payload: Partial<LeadSuggestionSettings>): Promise<LeadSuggestionSettings> {
    return apiClient.patch('/lead-suggestions/settings/', payload);
  },

  async getProfilePreview(): Promise<Record<string, unknown>> {
    return apiClient.get('/lead-suggestions/settings/profile-preview/');
  },

  async list(params: {
    status?: string;
    source?: string;
    sort?: string;
    min_score?: number;
    city?: string;
    state?: string;
    customer_type?: string;
  } = {}): Promise<LeadSuggestion[]> {
    const query = new URLSearchParams();
    if (params.status) query.set('status', params.status);
    if (params.source) query.set('source', params.source);
    if (params.sort) query.set('sort', params.sort);
    if (params.min_score != null) query.set('min_score', String(params.min_score));
    if (params.city) query.set('city', params.city);
    if (params.state) query.set('state', params.state);
    if (params.customer_type) query.set('customer_type', params.customer_type);
    const suffix = query.toString() ? `?${query.toString()}` : '';
    const response = await apiClient.get(`/lead-suggestions/${suffix}`) as
      | LeadSuggestion[]
      | PaginatedLeadSuggestionResponse;
    if (Array.isArray(response)) return response;
    return Array.isArray(response?.results) ? response.results : [];
  },

  async generate(): Promise<LeadSuggestionGenerateResult> {
    const result: LeadSuggestionGenerateResult = await apiClient.post('/lead-suggestions/generate/', {});
    // Stash the run's terminal reason so the (separate) list page can explain an empty
    // state without a dedicated last-run endpoint.
    try {
      if (result?.terminal_reason) {
        sessionStorage.setItem('lp_last_terminal_reason', String(result.terminal_reason));
      } else {
        sessionStorage.removeItem('lp_last_terminal_reason');
      }
    } catch {
      /* sessionStorage unavailable — non-fatal */
    }
    return result;
  },

  async acceptSuggestion(id: string) {
    return apiClient.post(`/lead-suggestions/${id}/accept/`, {});
  },

  async rejectSuggestion(id: string, rejection_reason?: RejectionReason) {
    return apiClient.post(`/lead-suggestions/${id}/reject/`, { rejection_reason: rejection_reason || '' });
  },

  async archiveSuggestion(id: string) {
    return apiClient.post(`/lead-suggestions/${id}/archive/`, {});
  },

  async convertToLead(id: string) {
    return apiClient.post(`/lead-suggestions/${id}/convert-to-lead/`, {});
  },

  async saveSuggestion(id: string) {
    return apiClient.post(`/lead-suggestions/${id}/save/`, {});
  },

  async dismissSuggestion(id: string) {
    return apiClient.post(`/lead-suggestions/${id}/dismiss/`, {});
  },

  async generateEmailSuggestion(payload: {
    email_type: string;
    lead_suggestion_id?: string;
    client_id?: string;
    lead_id?: string;
  }): Promise<EmailSuggestion> {
    return apiClient.post('/email-suggestions/generate/', payload);
  },

  async saveEmailDraft(payload: { email_suggestion_id: string }): Promise<{ draft_id: string }> {
    return apiClient.post('/email-suggestions/save-draft/', payload);
  },
};
